"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getBrandList, getCategoryList } from "@/lib/api/listings";
import { useGetHomeInfo, useCardFavorites } from "@/lib/api/hooks";
import { useI18n, getLocalizedField } from "@/i18n";
import { useLangHref } from "@/i18n/useLangHref";
const HERO_BG = "https://iyoubucket.oss-cn-hangzhou.aliyuncs.com/guinee_mall/hero-africa-desktop.jpg";

/* ─────────────────────────── components ─────────────────────────── */
/* ──────────────── 骨架屏辅助（只骨架数据卡片区，标题/描述/查看全部用真实 header） ──────────────── */
const ARROW_SVG = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16}><path d="M5 12h14M12 5l7 7-7 7"/></svg>);

function CategorySectionSkeleton() {
    const { t } = useI18n();
    const langHref = useLangHref();
    return (<section className="section" style={{ marginTop: 16 }}>
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("sections.browseByCategory")}</div>
            <h2 className="section-title">{t("sections.browseByCategory")}</h2>
            <p className="section-subtitle">{t("sections.browseByCategoryDesc")}</p>
          </div>
          <Link href={langHref("/search")} className="section-link">{t("sections.viewAll")} {ARROW_SVG}</Link>
        </div>
        <div className="grid grid-cols-3">
          {[0,1,2].map((i) => (<div key={i} className="sk-category-card">
              <span className="sk sk-block sk-category-bg"/>
              <div className="sk-category-body">
                <span className="sk sk-block sk-category-title"/>
                <span className="sk sk-block sk-category-desc"/>
                <span className="sk sk-block sk-category-count"/>
              </div>
            </div>))}
        </div>
      </div>
    </section>);
}

function BrandsSectionSkeleton() {
    const { t } = useI18n();
    return (<section className="section section-sm" style={{ background: "var(--color-surface)" }}>
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("sections.topBrands")}</div>
            <h2 className="section-title">{t("sections.topBrands")}</h2>
            <p className="section-subtitle">{t("sections.topBrandsDesc")}</p>
          </div>
        </div>
        <div className="grid grid-cols-8">
          {Array.from({ length: 16 }).map((_, i) => (<div key={i} className="sk-brand-item"><span className="sk sk-brand-logo"/></div>))}
        </div>
      </div>
    </section>);
}

function CarGridSkeleton({ cols = 4, count = 4 }) {
    return (<div className={`grid grid-cols-${cols}`}>
      {Array.from({ length: count }).map((_, i) => (<div key={i} className="sk-car-card">
        <span className="sk sk-block sk-car-image"/>
        <div className="sk-car-body">
          <span className="sk sk-block sk-car-title"/>
          <span className="sk sk-block sk-car-sub"/>
          <span className="sk sk-block sk-car-price"/>
          <div className="sk-car-meta"><span className="sk"/><span className="sk"/><span className="sk"/></div>
        </div>
      </div>))}
    </div>);
}

function FeaturedSectionSkeleton() {
    const { t } = useI18n();
    const langHref = useLangHref();
    return (<section className="section">
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("sections.featuredListings")}</div>
            <h2 className="section-title">{t("sections.featuredListings")}</h2>
            <p className="section-subtitle">{t("sections.featuredListingsDesc")}</p>
          </div>
          <Link href={langHref("/search")} className="section-link">{t("sections.viewAll")} {ARROW_SVG}</Link>
        </div>
        <CarGridSkeleton cols={4} count={4}/>
      </div>
    </section>);
}

function EvSectionSkeleton() {
    const { t } = useI18n();
    const langHref = useLangHref();
    return (<section className="section" style={{ background: "linear-gradient(135deg, #F5FCE9 0%, #FFFFFF 100%)" }}>
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow" style={{ color: "var(--color-success)" }}>⚡ {t("sections.electricVehicles")}</div>
            <h2 className="section-title">{t("sections.electricVehicles")}</h2>
            <p className="section-subtitle">{t("sections.electricVehiclesDesc")}</p>
          </div>
          <Link href={langHref("/search?fuel=ev")} className="section-link">{t("sections.viewAll")} {ARROW_SVG}</Link>
        </div>
        <CarGridSkeleton cols={3} count={3}/>
      </div>
    </section>);
}

function LatestSectionSkeleton() {
    const { t } = useI18n();
    return (<section className="section">
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("sections.latestListings")}</div>
            <h2 className="section-title">{t("sections.latestListings")}</h2>
            <p className="section-subtitle">{t("sections.latestListingsDesc")}</p>
          </div>
        </div>
        <CarGridSkeleton cols={4} count={4}/>
      </div>
    </section>);
}

function DealersSectionSkeleton() {
    const { t } = useI18n();
    const langHref = useLangHref();
    return (<section className="section" style={{ background: "var(--color-surface)" }}>
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("sections.verifiedDealers")}</div>
            <h2 className="section-title">{t("sections.verifiedDealers")}</h2>
            <p className="section-subtitle">{t("sections.verifiedDealersDesc", { count: 0 })}</p>
          </div>
          <Link href={langHref("/dealers")} className="section-link">{t("sections.viewAll")} {ARROW_SVG}</Link>
        </div>
        <div className="grid grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="sk-dealer-card">
            <div className="sk-dealer-head">
              <span className="sk sk-dealer-avatar"/>
              <div>
                <span className="sk sk-block sk-dealer-title"/>
                <span className="sk sk-block sk-dealer-desc"/>
                <span className="sk sk-block sk-dealer-desc2"/>
              </div>
            </div>
            <div className="sk-dealer-meta"><span className="sk"/><span className="sk"/></div>
            <div className="sk-dealer-stats">
              {[0,1,2,3].map((j) => (<div key={j} className="sk-dealer-stat">
                <span className="sk sk-block sk-dealer-stat-num"/>
                <span className="sk sk-block sk-dealer-stat-label"/>
              </div>))}
            </div>
            <div className="sk-dealer-footer"><span className="sk sk-block sk-dealer-btn"/></div>
          </div>))}
        </div>
      </div>
    </section>);
}

function BlogSectionSkeleton() {
    const { t } = useI18n();
    const langHref = useLangHref();
    return (<section className="section">
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("sections.fromTheBlog")}</div>
            <h2 className="section-title">{t("sections.fromTheBlog")}</h2>
            <p className="section-subtitle">{t("sections.fromTheBlogDesc")}</p>
          </div>
          <Link href={langHref("/blog")} className="section-link">{t("sections.viewAll")} {ARROW_SVG}</Link>
        </div>
        <div className="grid grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (<div key={i} className="sk-blog-card">
            <span className="sk sk-block sk-blog-image"/>
            <div className="sk-blog-body">
              <span className="sk sk-block sk-blog-cat"/>
              <span className="sk sk-block sk-blog-title"/>
              <span className="sk sk-block sk-blog-title2"/>
              <span className="sk sk-block sk-blog-excerpt"/>
              <span className="sk sk-block sk-blog-excerpt2"/>
              <div className="sk-blog-meta"><span className="sk"/><span className="sk"/></div>
            </div>
          </div>))}
        </div>
      </div>
    </section>);
}

function Hero() {
    const { t, lang } = useI18n();
    const langHref = useLangHref();
    const searchHref = langHref("/search");

    return (<section className="hero">
      <div className="hero-picture">
        <Image className="hero-img" src={HERO_BG} alt="非洲夫妇与销售员在户外看车 — GuineeMall" fill priority sizes="100vw" style={{ objectFit: "cover", objectPosition: "center 45%" }}/>
      </div>
      <div className="hero-overlay"/>
      <div className="hero-decor">
        <div className="hero-decor-ball b1"/>
        <div className="hero-decor-ball b2"/>
      </div>

      <div className="container">
        <div className="hero-content fade-in">
          <div className="hero-eyebrow">
            <span className="dot"/>
            <span>{t("hero.eyebrow")}</span>
          </div>
          <h1 className="hero-title">
            <span className="line-1">{t("hero.titleLine1")}</span>
            <span className="line-2">{t("hero.titleLine2")}</span>
          </h1>
          <p className="hero-subtitle">
            {t("hero.subtitle")}
          </p>
          <div className="hero-cta">
            <Link href={langHref("/publish")} className="btn btn-primary btn-xl">
              {t("hero.publishCta")}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} width={18} height={18}>
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
            <Link href={langHref("/search")} className="btn btn-ghost btn-xl">
              {t("hero.browseCta")}
            </Link>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="hero-search fade-in fade-in-2">
          <div className="hero-search-item">
            <div className="hero-search-label">{t("hero.brand")}</div>
            <div className="hero-search-value">{t("hero.allBrands")}</div>
          </div>
          <div className="hero-search-item">
            <div className="hero-search-label">{t("hero.model")}</div>
            <div className="hero-search-value">{t("hero.allModels")}</div>
          </div>
          <div className="hero-search-item">
            <div className="hero-search-label">{t("hero.price")}</div>
            <div className="hero-search-value">{t("hero.anyPrice")}</div>
          </div>
          <Link href={searchHref} className="btn btn-accent btn-lg">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={18} height={18}>
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <span>{t("hero.search")}</span>
          </Link>
        </div>
      </div>
    </section>);
}
/** 把扁平分类列表构造成父子树：parentId=0 的作为顶级，子分类归到对应父级 */
function buildCategoryTree(list) {
    const all = Array.isArray(list) ? list : [];
    const byId = new Map();
    all.forEach((c) => byId.set(c.id, { ...c, children: [] }));
    const parents = [];
    all.forEach((c) => {
        const node = byId.get(c.id);
        if (c.parentId === 1) {
            console.log('c.parentId === 1 || c.parentId == null', c)
            parents.push(node);
        } else if (byId.has(c.parentId)) {
            byId.get(c.parentId).children.push(node);
        } 
    });
    const sortFn = (a, b) => (a.sort ?? 0) - (b.sort ?? 0);
    parents.sort(sortFn);
    parents.forEach((p) => p.children.sort(sortFn));
    return parents;
}

function CategorySection() {
    const { t, lang } = useI18n();
    const langHref = useLangHref();
    const [tree, setTree] = useState([]);     // 构造好的父子树
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        getCategoryList('car')
            .then((res) => {
                if (!active) return;
                const raw = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
                setTree(buildCategoryTree(raw));
            })
            .catch(() => { /* 静默：用 fallback */ })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    // 兜底：接口没数据时用原来的三张占位卡（补三语字段）
    const FALLBACK = [
        { id: "fallback-1", categoryNameZh: "乘用车", categoryNameEn: "Cars", categoryNameFr: "Voitures", type: "car", icon: "🚗", children: [
            { categoryNameZh: "轿车", categoryNameEn: "Sedan", categoryNameFr: "Berline" },
            { categoryNameZh: "SUV", categoryNameEn: "SUV", categoryNameFr: "SUV" },
            { categoryNameZh: "MPV", categoryNameEn: "MPV", categoryNameFr: "Monospace" },
            { categoryNameZh: "皮卡", categoryNameEn: "Pickup", categoryNameFr: "Pickup" },
        ]},
        { id: "fallback-2", categoryNameZh: "摩托车", categoryNameEn: "Motorcycles", categoryNameFr: "Motos", type: "car", icon: "🏍️", children: [
            { categoryNameZh: "街车", categoryNameEn: "Street", categoryNameFr: "Roadster" },
            { categoryNameZh: "越野", categoryNameEn: "Off-road", categoryNameFr: "Tout-terrain" },
            { categoryNameZh: "跑车", categoryNameEn: "Sport", categoryNameFr: "Sportive" },
        ]},
        { id: "fallback-3", categoryNameZh: "商用车", categoryNameEn: "Trucks", categoryNameFr: "Camions", type: "car", icon: "🚚", children: [
            { categoryNameZh: "货车", categoryNameEn: "Truck", categoryNameFr: "Camion" },
            { categoryNameZh: "巴士", categoryNameEn: "Bus", categoryNameFr: "Bus" },
            { categoryNameZh: "叉车", categoryNameEn: "Forklift", categoryNameFr: "Chariot" },
        ]},
    ];
    const roots = tree.length > 0 ? tree : FALLBACK;

    // 给顶级分类选 icon：优先用 category.icon 字段，否则按 type/名字映射默认
    const iconSvg = (node) => {
        const raw = node?.icon;
        if (raw && /^https?:\/\//.test(raw)) {
            return <img src={raw} alt="" width={24} height={24} style={{ width: 24, height: 24 }} />;
        }
        if (raw && raw.length <= 4) return <span style={{ fontSize: 22 }}>{raw}</span>; // emoji

        const localName = getLocalizedField(node, "categoryName", lang);
        const name = localName.toLowerCase();
        const type = (node?.type || "").toLowerCase();
        if (name.includes("乘") || name.includes("car") || name.includes("voiture") || name.includes("sedan") || name.includes("berline")) {
            return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={24} height={24}>
                <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/>
                <circle cx="6.5" cy="16.5" r="2.5"/>
                <circle cx="16.5" cy="16.5" r="2.5"/>
            </svg>);
        }
        if (name.includes("摩托") || name.includes("bike") || name.includes("moto") || type === "bike") {
            return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={24} height={24}>
                <circle cx="18.5" cy="17.5" r="3.5"/>
                <circle cx="5.5" cy="17.5" r="3.5"/>
                <circle cx="15" cy="5" r="1"/>
                <path d="M12 17.5V14l-3-3 4-3 2 3h2"/>
            </svg>);
        }
        if (name.includes("商") || name.includes("货") || name.includes("truck") || name.includes("camion")) {
            return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={24} height={24}>
                <path d="M10 17h4V5H2v12h3"/>
                <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5"/>
                <circle cx="7.5" cy="17.5" r="2.5"/>
                <circle cx="17.5" cy="17.5" r="2.5"/>
            </svg>);
        }
        return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={24} height={24}>
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M3 9h18M9 21V9"/>
        </svg>);
    };

    const cardBg = (idx) => {
        if (idx === 0) return "linear-gradient(135deg, var(--color-brand) 0%, var(--color-brand-dark) 100%)";
        if (idx === 1) return "linear-gradient(135deg, var(--color-brand) 0%, var(--color-brand-dark) 100%)";
        return "linear-gradient(135deg, var(--color-brand) 0%, var(--color-brand-dark) 100%)";
    };

    return (<section className="section" style={{ marginTop: 16 }}>
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("sections.browseByCategory")}</div>
            <h2 className="section-title">{t("sections.browseByCategory")}</h2>
            <p className="section-subtitle">{t("sections.browseByCategoryDesc")}</p>
          </div>
          <Link href={langHref("/search")} className="section-link">
            {t("sections.viewAll")}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16}>
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>

        {loading && tree.length === 0 ? (
          <div className="category-grid" style={{ justifyContent: "center", color: "var(--color-text-muted)", padding: "40px 0" }}>{t("common.loading")}</div>
        ) : (
          <div className="category-grid">
            {roots.map((root, idx) => {
                const childNames = (root.children || []).map((c) => getLocalizedField(c, "categoryName", lang)).filter(Boolean);
                const desc = childNames.length > 0 ? childNames.join(" · ") : getLocalizedField(root, "categoryName", lang);
                const countLabel = root.count != null
                    ? `${root.count}${lang === "fr" ? " en vente" : lang === "en" ? " for sale" : ""} →`
                    : `${(root.children || []).length}${lang === "fr" ? " sous-catégories" : lang === "en" ? " subcategories" : ""} →`;
                const linkHref = root.id && String(root.id).startsWith("fallback")
                    ? `/search?type=${root.type || "cars"}`
                    : `/search?category=${root.id}`;
                const rootName = getLocalizedField(root, "categoryName", lang);
                return (
                    <Link key={root.id ?? rootName} href={linkHref} className="category-card">
                        <div className="category-card-bg" style={{ background: cardBg(idx) }} />
                        <div className="category-card-icon" style={idx === 0 ? undefined : { background: "var(--color-brand-darker)" }}>
                            {iconSvg(root)}
                        </div>
                        <div className="category-card-title">{rootName}</div>
                        <div className="category-card-desc">{desc}</div>
                        <div className="category-card-count">{countLabel}</div>
                    </Link>
                );
            })}
          </div>
        )}
      </div>
    </section>);
}
function BrandsSection({ brands: apiBrands = [] }) {
    const { t, lang } = useI18n();
    const brands = (apiBrands ?? []).slice(0, 16).map((b) => getLocalizedField(b, "name", lang));
    return (<section className="section section-sm" style={{ background: "var(--color-surface)" }}>
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("sections.topBrands")}</div>
            <h2 className="section-title">{t("sections.topBrands")}</h2>
            <p className="section-subtitle">{t("sections.topBrandsDesc")}</p>
          </div>
        </div>

        <div className="brand-grid">
          {brands.map((b, i) => (<div key={i} className="brand-tile">
              {b}
            </div>))}
        </div>
      </div>
    </section>);
}
function FeaturedSection({ listings = [] }) {
  const { t, lang } = useI18n();
  const langHref = useLangHref();
  const cars = listings || [];
  const { favoritedIds, handleFavorite, toastNode } = useCardFavorites(cars);
    return (<section className="section">
      {toastNode}
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("sections.featuredListings")}</div>
            <h2 className="section-title">{t("sections.featuredListings")}</h2>
            <p className="section-subtitle">{t("sections.featuredListingsDesc")}</p>
          </div>
          <Link href={langHref("/search")} className="section-link">
            {t("sections.viewAll")}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16}>
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>

        <div className="grid grid-cols-4">
          {cars.map((car, i) => {
            const fav = favoritedIds.has(car.id);
            return (<Link key={car.id ?? i} href={car.id ? langHref(`/detail/${car.id}`) : langHref("/detail")} className="car-card">
              <div className="car-card-image">
                <Image src={car.coverUrl} alt={car.title} fill sizes="(max-width: 768px) 100vw, 25vw" className="object-cover"/>
                <div className="car-card-badges">
                  {
                    car.isVerified === 1 && <span key='certification_mark' className={`tag tag-brand`}>
                      {t("car.verified")}
                    </span> 
                  }
                  {
                    car.isFeatured === 1 && <span key='promotion' className={`tag tag-success`}>
                      {t("car.promoted")}
                    </span> 
                  }
                  {
                    (car.fuelType === "electric" && car.condition === "new") && <span key='ev_new' className={`tag tag-new`}>
                      {t("car.evNew")}
                    </span> 
                  }
                </div>
                <div className="car-card-tour">📷 {car.imageCount}</div>
                <button className={`car-card-fav${fav ? " is-favorited" : ""}`} type="button" aria-label={t("car.favorites")} onClick={(e) => handleFavorite(e, car.id)}>
                  <svg viewBox="0 0 24 24" fill={fav ? "#EF4444" : "none"} stroke={fav ? "#EF4444" : "currentColor"} strokeWidth={2} width={16} height={16}>
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </button>
              </div>
              <div className="car-card-body">
                <div className="car-card-title">{car.title}</div>
                <div className="car-card-subtitle">{`📍 ${getLocalizedField(car, "cityName", lang)}${car.engineSize ? ` · ${car.engineSize}L` : ""} · ${car.transmission === "automatic" ? t("car.auto") : t("car.manual") }`}</div>
                <div className={`car-card-price ${car.priceClass}`}>{car.price}</div>
                <div className="car-card-meta">
                  <span key="mileage">📏 {car.mileage}{t("car.km")}</span>
                  <span key="fuelType">{`${car.fuelType === "electric" ? "⚡" : "⛽"} ${t(`car.${car.fuelType}`) || car.fuelType}`}</span>
                  <span key="carYear">📅 {car.carYear}</span>
                </div>
              </div>
            </Link>);
          })}
        </div>
      </div>
    </section>);
}
function PromoteCta() {
    const { t, lang } = useI18n();
    const langHref = useLangHref();
    return (<section className="section">
      <div className="container">
        <div className="cta-banner">
          <div className="cta-banner-content">
            <div className="section-title-eyebrow" style={{ color: "var(--color-brand-light)" }}>
              {t("promote.hero.eyebrow")}
            </div>
            <h3>{t("promote.hero.title")}</h3>
            <p>
              {t("promote.hero.subtitle")}
            </p>
            <div className="flex gap-3">
              <Link href={langHref("/promote")} className="btn btn-accent btn-lg">
                {t("promote.hero.ctaPlans")}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16}>
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              <Link href={langHref("/publish")} className="btn btn-lg" style={{
            background: "rgba(255,255,255,0.1)",
            color: "white",
            border: "1px solid rgba(255,255,255,0.2)",
        }}>
                {t("promote.hero.postFree")}
              </Link>
            </div>
          </div>
          <div className="cta-banner-stats">
            <div className="cta-banner-stat">
              <div className="cta-banner-stat-num">4×</div>
              <div className="cta-banner-stat-label">{t("promote.stats.avgSpeed")}</div>
            </div>
            <div className="cta-banner-stat">
              <div className="cta-banner-stat-num">50×</div>
              <div className="cta-banner-stat-label">{t("promote.stats.viewsBoost")}</div>
            </div>
          </div>
        </div>
      </div>
    </section>);
}
function EvSection({ evListings = [] }) {
  const { t, lang } = useI18n();
  const langHref = useLangHref();
  const cars = evListings || [];
  const { favoritedIds, handleFavorite, toastNode } = useCardFavorites(cars);
    return (<section className="section" style={{ background: "linear-gradient(135deg, #F5FCE9 0%, #FFFFFF 100%)" }}>
      {toastNode}
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow" style={{ color: "var(--color-success)" }}>
              ⚡ {t("sections.electricVehicles")}
            </div>
            <h2 className="section-title">{t("sections.electricVehicles")}</h2>
            <p className="section-subtitle">{t("sections.electricVehiclesDesc")}</p>
          </div>
          <Link href={langHref("/search?fuel=ev")} className="section-link">
            {t("sections.viewAll")}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16}>
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>

        <div className="grid grid-cols-3">
          {cars.map((car, i) => {
            const fav = favoritedIds.has(car.id);
            return (<Link key={`electric-vehicle-item${i}`} href={car.id ? langHref(`/detail/${car.id}`) : langHref("/detail")} className="car-card">
              <div className="car-card-image">
                <Image src={car.coverUrl} alt={car.title} fill sizes="(max-width: 768px) 100vw, 33vw" className="object-cover"/>
                <div className="car-card-badges">
                  <span className="tag" style={{ background: "var(--color-brand-darker)" }}>
                    ⚡ EV
                  </span>
                  {car.condition === "new" && (<span className="tag tag-new">NEW</span>)}
                </div>
                <button className={`car-card-fav${fav ? " is-favorited" : ""}`} type="button" aria-label={t("car.favorites")} onClick={(e) => handleFavorite(e, car.id)}>
                  <svg viewBox="0 0 24 24" fill={fav ? "#EF4444" : "none"} stroke={fav ? "#EF4444" : "currentColor"} strokeWidth={2} width={16} height={16}>
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </button>
              </div>
              <div className="car-card-body">
                <div className="car-card-title">{car.title}</div>
                <div className="car-card-subtitle">{`📍 ${getLocalizedField(car, "cityName", lang)}${car.batteryCapacity ? ` · ${car.batteryCapacity}kWh` : ""} · ${car.mileage}${t("car.km")}`}</div>
                <div className="car-card-price">GNF {car.priceGnf}M</div>
              </div>
            </Link>);
          })}
        </div>
      </div>
    </section>);
}
function LatestSection({ recommendedListings = [] }) {
  const { t, lang } = useI18n();
  const langHref = useLangHref();
  const cars = recommendedListings || [];
  const { favoritedIds, handleFavorite, toastNode } = useCardFavorites(cars);
    return (<section className="section">
      {toastNode}
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("sections.latestListings")}</div>
            <h2 className="section-title">{t("sections.latestListings")}</h2>
            <p className="section-subtitle">{t("sections.latestListingsDesc")}</p>
          </div>
        </div>

        <div className="grid grid-cols-4">
          {cars.map((car, i) => {
            const fav = favoritedIds.has(car.id);
            return (<Link key={car.id ?? i} href={car.id ? langHref(`/detail/${car.id}`) : langHref("/detail")} className="car-card">
              <div className="car-card-image">
                <Image src={car.coverUrl} alt={car.title} fill sizes="(max-width: 768px) 100vw, 25vw" className="object-cover"/>
                <div className="car-card-badges">
                  {
                    car.isVerified === 1 && <span key='certification_mark' className={`tag tag-brand`}>
                      {t("car.verified")}
                    </span> 
                  }
                  {
                    car.isFeatured === 1 && <span key='promotion' className={`tag tag-success`}>
                      {t("car.promoted")}
                    </span> 
                  }
                  {
                    (car.fuelType === "electric" && car.condition === "new") && <span key='ev_new' className={`tag tag-new`}>
                      {t("car.evNew")}
                    </span> 
                  }
                </div>
                <div className="car-card-tour">📷 {car.imageCount}</div>
                <button className={`car-card-fav${fav ? " is-favorited" : ""}`} type="button" aria-label={t("car.favorites")} onClick={(e) => handleFavorite(e, car.id)}>
                  <svg viewBox="0 0 24 24" fill={fav ? "#EF4444" : "none"} stroke={fav ? "#EF4444" : "currentColor"} strokeWidth={2} width={16} height={16}>
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </button>
              </div>
              <div className="car-card-body">
                <div className="car-card-title">{car.title}</div>
                <div className="car-card-subtitle">{`📍 ${getLocalizedField(car, "cityName", lang)}${car.engineSize ? ` · ${car.engineSize}L` : ""} · ${car.transmission === "automatic" ? t("car.auto") : t("car.manual") }`}</div>
                <div className={`car-card-price ${car.priceClass}`}>{car.price}</div>
                <div className="car-card-meta">
                  <span key="mileage">📏 {car.mileage}{t("car.km")}</span>
                  <span key="fuelType">{`${car.fuelType === "electric" ? "⚡" : "⛽"} ${t(`car.${car.fuelType}`) || car.fuelType}`}</span>
                  <span key="carYear">📅 {car.carYear}</span>
                </div>
              </div>
            </Link>);
          })}
        </div>
      </div>
    </section>);
}
const DEALER_LOGO_GRADIENTS = [
    "linear-gradient(135deg,#3DA005,#53c906)",
    "linear-gradient(135deg,#0EA5E9,#38BDF8)",
    "linear-gradient(135deg,#F59E0B,#FBBF24)",
    "linear-gradient(135deg,#8B5CF6,#A78BFA)",
    "linear-gradient(135deg,#FF7900,#FF3D00)",
    "linear-gradient(135deg,#2962FF,#5C7CFA)",
];

function DealersSection({ topDealers = [] , verifiedDealerCount}) {
    const { t, lang } = useI18n();
    const langHref = useLangHref();
    const dealers = (topDealers || []).slice(0, 6);
    if (dealers.length === 0) return null;
    return (<section className="section" style={{ background: "var(--color-surface)" }}>
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("sections.verifiedDealers")}</div>
            <h2 className="section-title">{t("sections.verifiedDealers")}</h2>
            <p className="section-subtitle">{t("sections.verifiedDealersDesc", { count: verifiedDealerCount ?? 0 })}</p>
          </div>
          <Link href={langHref("/dealers")} className="section-link">
            {t("sections.viewAll")}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16}>
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>

        <div className="grid grid-cols-3">
          {dealers.map((d, i) => {
            const logoIndex = i % DEALER_LOGO_GRADIENTS.length;
            const dealerName = getLocalizedField(d, "dealerName", lang);
            const initials = (dealerName || "D").trim().slice(0, 2).toUpperCase();
            const createDate = (d.createTime || "").split("T")[0];
            const responseRate = typeof d.responseRate === "number" ? d.responseRate : 1;
            return (<Link key={d.id ?? i} href={langHref(`/dealers/${d.id ?? i}`)} className="card" style={{ padding: 0, overflow: "hidden", textDecoration: "none", color: "inherit", display: "block" }}>
              <div style={{ padding: 24, display: "flex", gap: 16 }}>
                <div style={{
                    width: 56, height: 56, borderRadius: "var(--radius)",
                    background: DEALER_LOGO_GRADIENTS[logoIndex],
                    color: "white", display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 20, fontWeight: 800, flexShrink: 0,
                }}>{initials}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                    <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{dealerName}</h3>
                    {d.isVerified === 1 && <span className="tag tag-success" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>✓ {t("car.verified")}</span>}
                  </div>
                  <p style={{ fontSize: 13, color: "var(--color-text-muted)", margin: 0, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {getLocalizedField(d, "description", lang) || d.description || t("sections.noData")}
                  </p>
                </div>
              </div>

              <div style={{ padding: "0 24px 24px" }}>
                <div style={{ display: "flex", gap: 12, marginBottom: 16, fontSize: 12, color: "var(--color-text-muted)", flexWrap: "wrap" }}>
                  <span>📍 {getLocalizedField(d, "cityName", lang) || t("common.unknown")}</span>
                  {createDate && <span>📅 {createDate}</span>}
                </div>

                <div className="stats-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
                  <div className="stat-card" style={{ padding: 12, textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "var(--color-text)" }}>{d.onSaleCount ?? d.forSale ?? 0}</div>
                    <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{t("sections.onSale")}</div>
                  </div>
                  <div className="stat-card" style={{ padding: 12, textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "var(--color-primary)" }}>{d.totalSold ?? d.sold ?? 0}</div>
                    <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{t("sections.totalSold")}</div>
                  </div>
                  <div className="stat-card" style={{ padding: 12, textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "var(--color-brand)" }}>{d.rating ?? 0} ★</div>
                    <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{t("sections.rating")}</div>
                  </div>
                  <div className="stat-card" style={{ padding: 12, textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "var(--color-success)" }}>{(responseRate * 100).toFixed(0)}%</div>
                    <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{t("sections.responseRate")}</div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, paddingTop: 16, borderTop: "1px solid var(--color-border)" }}>
                  <span className="btn" style={{ flex: 1, height: 38, fontSize: 13, border: "1px solid #D0D5D0", background: "var(--color-surface)", color: "var(--color-text-secondary)", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", textDecoration: "none" }}>
                    {t("sections.viewShop")}
                  </span>
                </div>
              </div>
            </Link>);
          })}
        </div>
      </div>
    </section>);
}
function BlogSection({ latestArticles = [] }) {
    const { t, lang } = useI18n();
    const langHref = useLangHref();
    return (<section className="section">
      <div className="container">
        <div className="section-header">
          <div>
            <div className="section-title-eyebrow">{t("sections.fromTheBlog")}</div>
            <h2 className="section-title">{t("sections.fromTheBlog")}</h2>
            <p className="section-subtitle">{t("sections.fromTheBlogDesc")}</p>
          </div>
          <Link href={langHref("/blog")} className="section-link">
            {t("sections.viewAll")}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16}>
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>

        <div className="grid grid-cols-3">
          {latestArticles && latestArticles.length > 0 ? latestArticles.map((post) => {
            const rawDate = post.publishTime || post.createTime;
            const dateStr = rawDate ? String(rawDate).split("T")[0].slice(0, 10) : "";
            const rawViews = Number(post.viewCount ?? post.views ?? 0);
            const formatViews = (n) => {
              if (!n && n !== 0) return "";
              if (n >= 10000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "W";
              if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
              return String(n);
            };
            const viewsStr = formatViews(rawViews);
            const postTitle = getLocalizedField(post, "title", lang);
            const postSummary = getLocalizedField(post, "summary", lang);
            return (<Link key={post.id ?? postTitle} href={langHref(`/blog/${post.id ?? ""}`)} className="blog-card">
              <div className="blog-card-image">
                <Image src={post.coverImage} alt={postTitle} fill sizes="(max-width: 768px) 100vw, 33vw" className="object-cover"/>
              </div>
              <div className="blog-card-body">
                <span className="blog-card-category">{getLocalizedField(post, "categoryName", lang)}</span>
                <div className="blog-card-title">{postTitle}</div>
                <div className="blog-card-excerpt">{postSummary}</div>
                <div className="blog-card-meta">
                  <span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={14} height={14}>
                      <rect x="3" y="4" width="18" height="18" rx="2"/>
                      <path d="M16 2v4M8 2v4M3 10h18"/>
                    </svg>
                    {dateStr}
                  </span>
                  <span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={14} height={14}>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    {t("sections.readCount", { count: viewsStr })}
                  </span>
                </div>
              </div>
            </Link>);
          }) : null}
        </div>
      </div>
    </section>);
}
/* ─────────────────────────── page ─────────────────────────── */
export default function Home() {
    const { data, isLoading } = useGetHomeInfo();
    const homeInfo = data?.data ?? {};
    console.log('homeInfo.hotBrands', homeInfo.hotBrands)

    if (isLoading) {
        return (<>
          <Hero />
          <CategorySectionSkeleton/>
          <BrandsSectionSkeleton/>
          <FeaturedSectionSkeleton/>
          <EvSectionSkeleton/>
          <LatestSectionSkeleton/>
          <DealersSectionSkeleton/>
          <BlogSectionSkeleton/>
        </>);
    }

    return (<>
      <Hero hero={homeInfo.hero} />
      <CategorySection />
      <BrandsSection brands={homeInfo.hotBrands} />
      <FeaturedSection listings={homeInfo.featuredListings} />
      <PromoteCta/>
      <EvSection evListings={homeInfo.evListings}/>
      <LatestSection recommendedListings={homeInfo.recommendedListings}/>
      <DealersSection topDealers={homeInfo.topDealers} verifiedDealerCount={homeInfo.verifiedDealerCount}/>
      <BlogSection latestArticles={homeInfo.latestArticles}/>
    </>);
}
